import { useParams, useNavigate } from 'react-router'
import { useState, useEffect } from 'react'
import { trpc } from '../lib/trpc'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card'
import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'
import { Label } from '../components/ui/label'
import { CheckCircle, UserPlus, AlertCircle, Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import { useAuthStore } from '../stores/auth-store'
import KrushrLogo from '../components/common/KrushrLogo'

export default function AcceptInvite() {
  const { token } = useParams<{ token: string }>()
  const navigate = useNavigate()
  const { login } = useAuthStore()
  
  const [isLoading, setIsLoading] = useState(true)
  const [isAccepting, setIsAccepting] = useState(false)
  const [invitation, setInvitation] = useState<any>(null)
  const [userExists, setUserExists] = useState(false)
  const [error, setError] = useState<string | null>(null)
  
  const [formData, setFormData] = useState({
    name: '',
    password: '',
    confirmPassword: ''
  })
  
  const { data: invitationData, error: invitationError } = trpc.auth.getInvitationByToken.useQuery(
    { token: token || '' },
    { 
      enabled: !!token,
      retry: false,
      onSuccess: (data) => {
        setInvitation(data.invitation)
        setUserExists(data.userExists)
        setIsLoading(false)
      },
      onError: (error) => {
        setError(error.message)
        setIsLoading(false)
      }
    }
  )
  
  const acceptInvitation = trpc.auth.acceptInvitation.useMutation({
    onSuccess: (data) => {
      toast.success(`Welcome to ${data.workspace.name}!`)
      
      // Log the user in
      login(data.user, data.token)
      
      // Redirect to workspace
      navigate('/workspace')
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to accept invitation')
      setIsAccepting(false)
    }
  })
  
  const handleAccept = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!userExists) {
      // Validate new user form
      if (!formData.name || !formData.password) {
        toast.error('Please fill in all required fields')
        return
      }
      
      if (formData.password !== formData.confirmPassword) {
        toast.error('Passwords do not match')
        return
      }
      
      if (formData.password.length < 8) {
        toast.error('Password must be at least 8 characters')
        return
      }
    }
    
    setIsAccepting(true)
    acceptInvitation.mutate({
      token: token || '',
      name: userExists ? undefined : formData.name,
      password: userExists ? undefined : formData.password,
    })
  }
  
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-krushr-gray-50 to-krushr-gray-100 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-krushr-primary mx-auto mb-4" />
          <p className="text-krushr-gray-600">Loading invitation...</p>
        </div>
      </div>
    )
  }
  
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-krushr-gray-50 to-krushr-gray-100 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardHeader className="text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-8 h-8 text-red-600" />
            </div>
            <CardTitle>Invalid Invitation</CardTitle>
            <CardDescription className="mt-2">
              {error}
            </CardDescription>
          </CardHeader>
          <CardContent className="text-center">
            <Button onClick={() => navigate('/login')}>
              Go to Login
            </Button>
          </CardContent>
        </Card>
      </div>
    )
  }
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-krushr-gray-50 to-krushr-gray-100 flex items-center justify-center p-4">
      <Card className="max-w-md w-full">
        <CardHeader className="text-center">
          <div className="mb-6">
            <KrushrLogo size="lg" showText />
          </div>
          <div className="w-16 h-16 bg-krushr-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <UserPlus className="w-8 h-8 text-krushr-primary" />
          </div>
          <CardTitle>You're invited!</CardTitle>
          <CardDescription className="mt-2">
            <strong>{invitation?.invitedBy.name}</strong> has invited you to join<br />
            <strong className="text-krushr-primary">{invitation?.workspace.name}</strong>
          </CardDescription>
        </CardHeader>
        
        <CardContent>
          <form onSubmit={handleAccept} className="space-y-4">
            {!userExists ? (
              <>
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={invitation?.email}
                    disabled
                    className="bg-gray-50"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="name">Your Name *</Label>
                  <Input
                    id="name"
                    type="text"
                    placeholder="John Doe"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    required
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="password">Create Password *</Label>
                  <Input
                    id="password"
                    type="password"
                    placeholder="At least 8 characters"
                    value={formData.password}
                    onChange={(e) => setFormData({...formData, password: e.target.value})}
                    minLength={8}
                    required
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">Confirm Password *</Label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    placeholder="Confirm your password"
                    value={formData.confirmPassword}
                    onChange={(e) => setFormData({...formData, confirmPassword: e.target.value})}
                    minLength={8}
                    required
                  />
                </div>
              </>
            ) : (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                  <div className="text-sm">
                    <p className="font-medium text-green-900">Account exists</p>
                    <p className="text-green-700 mt-1">
                      You already have an account with {invitation?.email}. 
                      Click below to accept the invitation and join the workspace.
                    </p>
                  </div>
                </div>
              </div>
            )}
            
            <div className="pt-4">
              <Button
                type="submit"
                className="w-full"
                disabled={isAccepting}
              >
                {isAccepting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Accepting...
                  </>
                ) : (
                  <>
                    {userExists ? 'Accept Invitation' : 'Create Account & Join'}
                  </>
                )}
              </Button>
            </div>
            
            <div className="text-center text-sm text-muted-foreground">
              Already have an account?{' '}
              <a href="/login" className="text-krushr-primary hover:underline">
                Sign in
              </a>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}